<template>
  <div class="layout" :class="{ 'layout--flow': isFlowPage }">
    <NotificationsPortal />
    <PaymentOperationNotice />
    <AppHeader />

    <PullToRefresh :disabled="layoutStore.pullToRefreshDisabled">
      <main class="layout__main" :class="{ 'layout__main--loading': isNavigating }">
        <div v-if="profileStore.needsSponsor" class="layout__sponsor-notice" role="status">
          <span>{{ $t('sponsorRequired.banner') }}</span>
          <BaseButton monochrome size="sm" @click="profileStore.sponsorPromptOpen = true">
            {{ $t('sponsorRequired.add') }}
          </BaseButton>
        </div>
        <RouterView v-slot="{ Component }" :route="route">
          <!-- Mount the destination before router scroll restoration; out-in
               defers it past the router's nextTick and clamps Back positions. -->
          <Transition name="page-fade">
            <component :is="Component" />
          </Transition>
        </RouterView>
        <MascotLoader contained :active="isNavigating" :label="$t('common.loading')" />
      </main>
    </PullToRefresh>

    <AppFooter v-if="!isFlowPage" />
    <AppBottomNav />
    <AppSidebar />

    <!-- Country Selection Modal -->
    <CountrySelectionModal
      :visible="showCountryModal && !profileStore.needsSponsor"
      :required="true"
      @select="onCountrySelect"
      @close="showCountryModal = false"
    />

    <!-- Intercom Support Widget (logic only, safe to catch inside layout) -->
    <IntercomWidget />

    <!-- Connection Error Modal (blocking) -->
    <ConnectionErrorModal :visible="showConnectionError" />

    <!-- TASK-10: Force-change password modal (blocking for MD5 users) -->
    <PasswordChangeRequiredModal />

  </div>
</template>


<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { useRoute } from 'vue-router'
import { NotificationsPortal } from '@space/notifications'

import '@/assets/scss/notifications.scss'

import PaymentOperationNotice from '@/modules/payment/components/PaymentOperationNotice.vue'
import AppHeader from '@/components/common/AppHeader.vue'
import BaseButton from '@/shared/components/base/BaseButton.vue'
import MascotLoader from '@/components/common/MascotLoader.vue'
import AppFooter from '@/components/common/AppFooter.vue'
import AppBottomNav from '@/components/navigation/AppBottomNav.vue'
import AppSidebar from '@/components/navigation/AppSidebar.vue'
import CountrySelectionModal from '@/modules/profile/components/CountrySelectionModal.vue'
import IntercomWidget from '@/components/common/IntercomWidget.vue'
import ConnectionErrorModal from '@/components/common/ConnectionErrorModal.vue'
import PullToRefresh from '@/components/common/PullToRefresh.vue'
import PasswordChangeRequiredModal from '@/modules/auth/components/PasswordChangeRequiredModal.vue'
import { useAppInitialization } from '@/shared/composables/useAppInitialization'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { useLayoutStore } from '@/store/layoutStore'
import { useTheme } from '@/composables/useTheme'
import { spaceSocket } from '@/services/spaceSocket'
import { formatFlowTerminology } from '@/shared/utils/flowTerminology'
import iziToast from 'izitoast'

const props = defineProps<{ route?: RouteLocationNormalizedLoaded }>()
const currentRoute = useRoute()
const isFlowPage = computed(() => /^\/chains(?:\/|$)/.test((props.route ?? currentRoute).path))

const { initializeApp } = useAppInitialization()
const profileStore = useProfileStore()
const layoutStore = useLayoutStore()

const showCountryModal = ref(false)
const showConnectionError = ref(false)
const isNavigating = computed(() => layoutStore.isNavigating)

// Connection error event handler
function onConnectionError() {
  showConnectionError.value = true
}

// Watch for profile loading: initialize WebSocket & show country modal if needed
watch(
  () => profileStore.profile,
  (profile) => {
    console.log('[CountryPopup] Profile loaded:', profile?.country, 'hasCountry:', profileStore.hasCountry)
    
    if (!profile) return
    
    // Initialize WebSocket connection when ws config is available
    if (profile.ws) {
      spaceSocket.init(profile.ws)

      // Subscribe to global broadcast channel (toast notifications)
      spaceSocket.subscribe('global', (data: any) => {
        const payload = typeof data === 'object' ? data : JSON.parse(data)

        // Handle redirect action
        if (payload.action === 'redirect' && payload.url) {
          window.location.href = payload.url
          return
        }

        // Show toast notification
        if (payload.title) {
          iziToast.info({
            title: formatFlowTerminology(payload.title),
            message: formatFlowTerminology(payload.text || ''),
            position: 'topRight',
            timeout: 7000,
            ...(payload.image ? { imageWidth: 56, image: payload.image } : {}),
          })
        }
      })
    }
    
    // Show modal if no country is set
    if (!profileStore.hasCountry) {
      console.log('[CountryPopup] Showing modal - no country set')
      showCountryModal.value = true
    }
  },
  { immediate: true }
)

function onCountrySelect() {
  sessionStorage.setItem('country_popup_shown', 'true')
  showCountryModal.value = false
}

// Start profile loading in background - pages render their skeletons immediately
onMounted(() => {
  // Interiors follow the approved light redesign, including teleported dialogs.
  if (!currentRoute.meta.public) useTheme().setTheme('light')
  initializeApp()
  window.addEventListener('gonkagame:connection-error', onConnectionError)
})

onUnmounted(() => {
  window.removeEventListener('gonkagame:connection-error', onConnectionError)
  spaceSocket.disconnect()
})
</script>

<style scoped lang="scss">
.layout {
  &__sponsor-notice {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px;
    margin-bottom: 20px;
    border: 1px solid var(--ds-border);
    border-radius: var(--ds-radius-card);
    background: var(--ds-surface);
    color: var(--ds-text);
  }
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  // Removed height: 100vh and overflow-y: auto
  // Let body manage scroll to prevent sticky header + backdrop-filter repaint conflicts
  background: var(--hm-bg-base);

  &__main {
    flex: 1;
    padding: 10px 20px 40px;
    position: relative;
    isolation: isolate;
    // App content canvas: recessed grey (light) / deepest violet (dark) so the
    // white/violet panels on top separate by contrast and the canvas fills the
    // whole screen edge-to-edge (no white gutter around the page).
    background: var(--ds-surface-canvas);
    
    @media (max-width: 768px) {
      // Extra padding for bottom nav (64px height + some breathing room)
      padding: 0px 10px 88px;
    }
    
    &--loading {
      pointer-events: none;
    }
  }
}

// Page fade transition
.page-fade-enter-active {
  transition: opacity 0.15s ease-out;
}

.page-fade-leave-active {
  transition: opacity 0.1s ease-in;
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}

// Overlay fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
